/**
 * `useHelpFillable(formId, binding)` — how a reviewed form lets the help assistant propose values into it (plan-019
 * WAPP-HOST; ED-25; design §31.9.5; research `docs/research/help-form-fill-hook.md` §3–§6).
 *
 * Called by the component that owns the controls, once per mounted step, with a binding per field that is ON SCREEN
 * now (`undefined` = not mounted): the field's current value, the form's OWN setter (the one its `onChange` calls),
 * optionally `unset` (back to "not answered") and `accepts` (what the form offers right now). Types bind it to the
 * registry (`packages/domain/src/help/form-registry.ts`): a code the form does not register is an excess property, a
 * setter of the wrong type is a type error, and a never-fill form has no `HelpFormId` at all.
 *
 * Registration is per field, merged under the form id, keyed by this component (`useId()`) and the router `href`;
 * it is re-made only when the set of mounted fields changes, never on a value change, and removed on unmount — so a fill
 * after a step change, a route change or an unmount is refused. Outside the provider (`./fill-context.tsx`) the hook
 * does nothing and marks nothing.
 *
 * `mark(code)` spreads onto the control (or a composite control's wrapper): `data-wt-hp-field` always, and
 * `data-wt-hp-filled="true"` while the field still holds the value the last fill put there — computed in render, so
 * the person's edit clears it. The hook never calls `focus()`, `blur()` or `scrollIntoView()`.
 */
import type { FieldCodeOf, HelpFillValue, HelpFormId, ValueOf } from "@wetalk/domain";
import { HELP_FORM_MARK_ATTRIBUTE } from "@wetalk/help-widget/path";
import { use, useEffect, useId, useLayoutEffect, useRef, useSyncExternalStore, type RefObject } from "react";

import { HelpFillContext } from "./fill-context.js";
import { sameFillValue, type HelpFieldPort } from "./fill-registry.js";

/** One mounted field, as the form binds it. */
export interface FieldBinding<V> {
  /** The current value; `null` while the field holds no answer (the wizard's name before it is typed). */
  readonly value: V | null;
  /** The form's own setter — the one its `onChange` calls. */
  readonly set: (next: V) => void;
  /** Back to "not answered", for an undo. */
  readonly unset?: () => void;
  /** What the form accepts right now beyond the registry's static bounds (the offered languages, options). */
  readonly accepts?: (next: V) => boolean;
}

/** A form's mounted fields: every key a registered code, `undefined` for a field not on screen now. */
export type HelpBinding<F extends HelpFormId> = {
  readonly [C in FieldCodeOf<F>]?: FieldBinding<ValueOf<F, C>> | undefined;
};

/** What `mark(code)` spreads onto a control. */
export interface HelpFieldMark {
  readonly [HELP_FORM_MARK_ATTRIBUTE.field]?: string;
  readonly [HELP_FORM_MARK_ATTRIBUTE.filled]?: "true";
}

export interface HelpFillMarks<F extends HelpFormId> {
  mark(code: FieldCodeOf<F>): HelpFieldMark;
}

type LooseBinding = Readonly<Record<string, FieldBinding<HelpFillValue> | undefined>>;

const NO_MARK: HelpFieldMark = Object.freeze({});

function noSubscription(): () => void {
  return () => undefined;
}

function noSnapshot(): number {
  return 0;
}

/** The port a registration hands the registry: every function reads the form's latest render. */
function portOf(latest: RefObject<LooseBinding>, code: string): HelpFieldPort {
  return {
    read: () => {
      const binding = latest.current[code];
      return binding === undefined ? null : binding.value;
    },
    accepts: (value) => {
      const binding = latest.current[code];
      if (binding === undefined) return false;
      return binding.accepts === undefined ? true : binding.accepts(value);
    },
    set: (value) => {
      latest.current[code]?.set(value);
    },
    get unset() {
      const unset = latest.current[code]?.unset;
      return unset === undefined ? null : unset;
    },
  };
}

export function useHelpFillable<F extends HelpFormId>(formId: F, binding: HelpBinding<F>): HelpFillMarks<F> {
  const context = use(HelpFillContext);
  const registry = context === null ? null : context.registry;
  const href = context === null ? "" : context.href;
  const owner = useId();
  const loose = binding as unknown as LooseBinding;
  const latest = useRef<LooseBinding>(loose);
  useLayoutEffect(() => {
    latest.current = loose;
  });

  const mounted = Object.keys(loose)
    .filter((code) => loose[code] !== undefined)
    .sort();
  const mountedKey = mounted.join("|");
  useEffect(() => {
    if (registry === null) return undefined;
    const field: Record<string, HelpFieldPort> = {};
    for (const code of mountedKey === "" ? [] : mountedKey.split("|")) field[code] = portOf(latest, code);
    return registry.register({ owner, formId, href, field });
  }, [registry, owner, formId, href, mountedKey]);

  // Typed input changes what the page context says; the widget re-sends it (debounced).
  const valueKey = registry === null ? "" : JSON.stringify(mounted.map((code) => loose[code]?.value ?? null));
  useEffect(() => {
    registry?.valuesChanged();
  }, [registry, valueKey]);

  // A fill or an undo re-renders the form, so the marks follow.
  useSyncExternalStore(registry === null ? noSubscription : registry.subscribe, registry === null ? noSnapshot : registry.snapshot, noSnapshot);

  return {
    mark(code) {
      if (registry === null) return NO_MARK;
      const filledValue = registry.filledValue(formId, code);
      const current = loose[code]?.value;
      return filledValue !== undefined && sameFillValue(current, filledValue)
        ? { [HELP_FORM_MARK_ATTRIBUTE.field]: code, [HELP_FORM_MARK_ATTRIBUTE.filled]: "true" }
        : { [HELP_FORM_MARK_ATTRIBUTE.field]: code };
    },
  };
}
